// Renders a resource's rich body (the [[...]] token string from richBody.js) for
// the viewer: plain text with inline image / video / link blocks between the rows.
// Mirrors the Eng Tour's RichText so authored content looks the same here.
import { picUrl } from './placeImages.js';
import { parseBlocks, youtubeId } from './richBody.js';
import { safeHref } from '../utils/safeUrl.js';

// A YouTube URL becomes an embedded iframe (a <video> can't play YouTube); any
// other URL is a direct HTML5 <video>. `autoplay` starts it muted (browser rule).
export function VideoEmbed({ url, autoplay = false }) {
  const yt = youtubeId(url);
  if (yt) {
    const params = autoplay ? '?autoplay=1&mute=1&playsinline=1' : '';
    return (
      <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${yt}${params}`}
          title="Video"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return <video src={url} controls autoPlay={autoplay} muted={autoplay} playsInline={autoplay} style={{ width: '100%', display: 'block' }} />;
}

export default function RichText({ text }) {
  if (!text) return null;
  const blocks = parseBlocks(text);
  return (
    <div className="wd-rich">
      {blocks.map((b) => {
        if (b.type === 'link') {
          // Authored text going straight into an href: only http(s), a site
          // path or mailto/tel survive, anything else is an inert label.
          const href = safeHref(b.url);
          return (
            <a key={b._id} className="wd-rich-link" href={href || undefined} target="_blank" rel="noopener noreferrer"
              style={{ cursor: href ? 'pointer' : 'default' }}>{b.label}</a>
          );
        }
        if (b.type === 'image') {
          if (!b.src) return null;
          return (
            <img key={b._id} className="wd-rich-img" src={picUrl(b.src)} alt=""
              style={b.height ? { height: `${b.height}px`, objectFit: 'cover' } : undefined}
              onError={(e) => { e.target.style.display = 'none'; }} />
          );
        }
        if (b.type === 'video') {
          if (!b.url) return null;
          return <div key={b._id} className="wd-rich-video"><VideoEmbed url={b.url} autoplay={b.autoplay} /></div>;
        }
        const val = String(b.value || '').replace(/^\n+|\n+$/g, '');
        return val ? <p key={b._id} className="wd-rich-text">{val}</p> : null;
      })}
    </div>
  );
}
